import useCheckAuth from './useCheckAuth';
import { useMemo } from 'react';
import type { Permission } from 'types/shared';

const ACTIONS = ['add', 'view', 'change', 'delete'] as const;
const MODELS = [
  // Facebook
  'flow',
  'originaltextcreative',
  'campaignid',
  'adlog',
  'facebookadaccount',
  'facebookpage',
  'userprofile',
  'gototest',
  'partnerships',

  // TikTok
  'tiktoktext',
  'tiktokflow',
  'tiktokgototest',
] as const;

type Action = typeof ACTIONS[number];
type Models = typeof MODELS[number];
type AccessKey = `accessTo${Capitalize<Action>}${Capitalize<Models>}`;
type AccessKeySpecial = 'accessToGenerateId';

type AccessByPermissionReturnType = Record<AccessKey, boolean> & {
  [key in AccessKeySpecial]?: boolean;
};

const usePermissionGuard = (): AccessByPermissionReturnType => {
  const { permissions, isSuperuser } = useCheckAuth();

  return useMemo(() => {
    const _capitalizeFirstLetter = (string: string) => {
      return string.charAt(0).toUpperCase() + string.slice(1);
    };

    const _getPermissionKey = (action: Action, entity: Models): Permission => {
      return `${action}_${entity}` as Permission;
    };

    const _getAccessKey = (action: Action, model: Models) => {
      return `accessTo${_capitalizeFirstLetter(action)}${_capitalizeFirstLetter(model)}` as AccessKey;
    };

    const _hasPermission = (currentPermission: Permission) =>
      isSuperuser || permissions?.includes(currentPermission);

    const _hasMultiplePermissions = (...perms: Permission[]) =>
      isSuperuser || perms.every(p => permissions?.includes(p));

    const access: Partial<AccessByPermissionReturnType> = {};

    ACTIONS.forEach(action => {
      MODELS.forEach(model => {
        const permissionKey = _getPermissionKey(action, model);
        const accessKey = _getAccessKey(action, model);

        access[accessKey] = _hasPermission(permissionKey);
      });
    });

    access.accessToGenerateId = _hasMultiplePermissions('change_campaignid', 'view_campaignid');
    console.log({ access });
    return access as AccessByPermissionReturnType;
  }, [permissions, isSuperuser]);
};

export default usePermissionGuard;
